import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { disputableDebits, disputes, raiseDispute, reportFraud } from '@/api/bankServices';
import type { Dispute } from '@/types/phase4.types';
import '../phase4.css';

type Debit = Awaited<ReturnType<typeof disputableDebits>>[number];

const Disputes = ({ impsOnly = false }: { impsOnly?: boolean }) => {
  const navigate = useNavigate();
  const [debits, setDebits] = useState<Debit[]>([]);
  const [records, setRecords] = useState<Dispute[]>([]);
  const [reason, setReason] = useState('I do not recognise this transaction');
  const [description, setDescription] = useState('');
  const [fraudReason, setFraudReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    Promise.all([disputableDebits(impsOnly), disputes()])
      .then(([items, current]) => { setDebits(items); setRecords(current); })
      .catch(() => toast.error('Could not load disputes.'));
  };
  useEffect(load, [impsOnly]);

  const file = async (debit: Debit) => {
    setBusy(true);
    try {
      await raiseDispute({
        txnId: debit.id,
        txnDescription: debit.description,
        amountRupees: debit.amountRupees,
        txnDate: debit.date,
        kind: impsOnly ? 'IMPS' : 'OTHER',
        reason,
        description: description || reason,
      });
      toast.success('Complaint registered');
      setDescription('');
      load();
    } catch (err) { toast.error(err instanceof Error ? err.message : 'Could not register complaint.'); }
    finally { setBusy(false); }
  };

  const fraud = async () => {
    if (!fraudReason.trim()) return;
    try {
      const result = await reportFraud(fraudReason.trim());
      toast.success(`Fraud reported. Reference ${result.reference}`);
      setFraudReason('');
      load();
    } catch (err) { toast.error(err instanceof Error ? err.message : 'Could not report fraud.'); }
  };

  return (
    <div className="nova-p4-page">
      <div className="nova-p4-header">
        <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={() => navigate('/profile/bank')}><FontAwesomeIcon icon={faArrowLeft} /></button>
        <h2>{impsOnly ? 'IMPS Dispute' : 'Disputes and Fraud'}</h2>
      </div>
      {!impsOnly && <div className="nova-p4-card">
        <h3>Report fraud</h3>
        <p className="nova-p4-muted">Reporting fraud immediately freezes all debits. Credits continue to come in.</p>
        <textarea className="form-control mb-2" rows={2} value={fraudReason} onChange={(e) => setFraudReason(e.target.value)} placeholder="Tell us what happened" />
        <button type="button" className="nova-p4-primary" disabled={!fraudReason.trim()} onClick={() => void fraud()}>Report fraud</button>
      </div>}
      <div className="nova-p4-card">
        <h3>{impsOnly ? 'Eligible IMPS transactions' : 'Eligible transactions'}</h3>
        {debits.length === 0 && <p className="nova-p4-muted">No eligible transactions found.</p>}
        {debits.map((debit) => (
          <div className="nova-p4-row" key={debit.id} style={{ cursor: 'default' }}>
            <span className="nova-p4-row-text"><span className="nova-p4-row-title">{debit.description}</span><span className="nova-p4-row-sub">{new Date(debit.date).toLocaleDateString('en-IN')} · ₹{debit.amountRupees.toLocaleString('en-IN')}</span></span>
            {debit.openComplaint ? <span className="nova-p4-status info">Open</span> : <button type="button" className="nova-p4-secondary" disabled={busy} onClick={() => void file(debit)}>Dispute</button>}
          </div>
        ))}
      </div>
      <div className="nova-p4-card">
        <h3>Complaint details</h3>
        <select className="form-control mb-2" value={reason} onChange={(e) => setReason(e.target.value)}><option>I do not recognise this transaction</option><option>Wrong amount</option><option>Cash not received</option><option>Service not received</option></select>
        <textarea className="form-control" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Additional details (optional)" />
      </div>
      {records.length > 0 && <div className="nova-p4-card"><h3>My complaints</h3>{records.map((record) => <div className="nova-p4-moneyline" key={record.id}><span>{record.reference}</span><span>{record.status.replace(/_/g, ' ')}</span></div>)}</div>}
    </div>
  );
};

export default Disputes;
